import clsx from 'clsx';
import {
  Clapperboard,
  Crosshair,
  MonitorPlay,
  Settings,
  ShieldCheck,
  Unlock,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { DEPLOY_ENV, FLOW, FLOW_DISPLAY_NAMES } from '../constants';
import { getCookie } from '../cookies';
import { useStore } from '../store';
import { showToast } from '../toast';
import { ReleaseLeaseModal } from './ReleaseLeaseModal';

export function Header() {
  const {
    operator,
    authUser,
    isAdmin,
    isAuditor,
    resetEpisodeSpecifyState,
    showEpisodeSpecify,
    enterAdminMode,
    showDashboard,
    showUserSelection,
    resetAnnotationSession,
    startOperatorRandom,
    statsCache,
    myStatsCache,
  } = useStore();

  const [showAdminQuick, setShowAdminQuick] = useState(false);
  const [isReleaseOpen, setIsReleaseOpen] = useState(false);
  const features = myStatsCache?.features ?? statsCache?.features;
  const manualLeaseRelease = features?.manual_lease_release === true;

  useEffect(() => {
    const checkAdminCookie = () => {
      const enabled = getCookie('admin_mode') === '1';
      setShowAdminQuick(enabled);
    };
    checkAdminCookie();
  }, []);

  const handleLogoClick = () => {
    if (isAdmin || operator) {
      void showDashboard();
    } else {
      void showUserSelection();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContinuousClick = () => {
    if (!operator) {
      showToast('Choose an operator first.');
      void showUserSelection();
      return;
    }
    resetAnnotationSession();
    void startOperatorRandom(operator);
  };

  const handleAuditClick = () => {
    if (!operator) {
      showToast('Choose an operator first.');
      void showUserSelection();
      return;
    }
    resetAnnotationSession();
    void useStore.getState().startAuditRandom(operator);
  };

  const handleEpisodeEntryClick = (intent: 'admin-readonly' | 'operator-claim') => {
    resetAnnotationSession();
    resetEpisodeSpecifyState();
    void showEpisodeSpecify(null, {
      autoSubmit: false,
      showViewerPlaceholder: false,
      intent,
    });
  };

  const handleAdminQuickClick = () => {
    void enterAdminMode();
  };

  const realUser = authUser?.user;
  const displayOperator = realUser || (isAdmin ? 'Admin Mode' : operator || 'No operator');

  return (
    <>
      {manualLeaseRelease && (
        <ReleaseLeaseModal isOpen={isReleaseOpen} onClose={() => setIsReleaseOpen(false)} />
      )}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-brand-border bg-brand-dark/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo Section */}
          <div className="flex items-center gap-6">
            <button
              onClick={handleLogoClick}
              type="button"
              className="group flex items-center gap-2 outline-none"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-orange text-white shadow-lg shadow-brand-orange/20 transition-transform group-hover:scale-105 group-active:scale-95">
                <Clapperboard className="h-5 w-5" />
              </div>
              <div className="hidden items-center gap-2 sm:flex">
                <span className="text-lg font-bold tracking-tight text-brand-text-primary group-hover:text-white transition-colors">
                  {FLOW_DISPLAY_NAMES[FLOW] ?? 'SecShot'}
                </span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none ${DEPLOY_ENV === 'test' ? 'bg-amber-500/20 text-amber-400' : DEPLOY_ENV === 'dev' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}`}
                >
                  {DEPLOY_ENV}
                </span>
              </div>
            </button>

            {/* Contextual Nav Items - Optional: Add Breadcrumbs or Tabs here later */}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button
                  onClick={() => handleEpisodeEntryClick('admin-readonly')}
                  className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-1.5 text-xs font-medium text-brand-text-secondary transition hover:bg-brand-surface hover:text-brand-text-primary hover:border-brand-text-secondary/20"
                  type="button"
                  title="View Episode"
                >
                  <MonitorPlay className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">View Episode</span>
                </button>
              )}

              <button
                onClick={() => handleEpisodeEntryClick('operator-claim')}
                className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-1.5 text-xs font-medium text-brand-text-secondary transition hover:bg-brand-surface hover:text-brand-text-primary hover:border-brand-text-secondary/20"
                type="button"
                title="Claim Episode"
              >
                <Crosshair className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Claim Episode</span>
              </button>

              <button
                onClick={handleContinuousClick}
                className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-1.5 text-xs font-medium text-brand-text-secondary transition hover:bg-brand-surface hover:text-brand-text-primary hover:border-brand-text-secondary/20"
                type="button"
                title="Random Mode"
              >
                <Zap className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Random</span>
              </button>

              {(isAuditor || isAdmin) && (
                <button
                  onClick={handleAuditClick}
                  className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-1.5 text-xs font-medium text-brand-text-secondary transition hover:bg-brand-surface hover:text-brand-text-primary hover:border-brand-text-secondary/20"
                  type="button"
                  title="Start Audit"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Audit</span>
                </button>
              )}

              {manualLeaseRelease && (
                <button
                  onClick={() => setIsReleaseOpen(true)}
                  className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-1.5 text-xs font-medium text-brand-text-secondary transition hover:bg-brand-surface hover:text-brand-text-primary hover:border-brand-text-secondary/20"
                  type="button"
                  title="Release my annotate or audit lease"
                >
                  <Unlock className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Release</span>
                </button>
              )}
            </div>

            <div className="h-6 w-px bg-brand-border mx-1"></div>

            {/* User Profile */}
            <div className="relative group">
              <div
                className={clsx(
                  'flex items-center gap-2 rounded-full border border-brand-border bg-brand-surface py-1.5 pl-2 pr-4',
                  isAdmin ? 'border-amber-500/30' : ''
                )}
              >
                <div
                  className={clsx(
                    'h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold',
                    isAdmin ? 'bg-amber-500 text-black' : 'bg-brand-text-secondary text-brand-dark'
                  )}
                >
                  {displayOperator.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs font-medium text-brand-text-primary max-w-[100px] truncate">
                  {displayOperator}
                </span>
              </div>
            </div>
            {showAdminQuick && (
              <button
                type="button"
                onClick={handleAdminQuickClick}
                className="ml-2 p-2 text-brand-text-secondary hover:text-brand-orange transition-colors"
                title="Admin Dashboard"
              >
                <Settings className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
